import React, {forwardRef} from 'react';

import {withCssModule} from '@pb/kit-util-with-css-module';

import {LabelBase} from './label-base';
import styles from './label.module.styl';

import type {NativeLabelProps} from './label-base';
import type {ComponentPropsWithoutRef, ReactNode} from 'react';


const classNames = withCssModule(styles);

export const Tags = {
    LABEL: 'Tags.LABEL',
    DIV: 'Tags.DIV',
    SPAN: 'Tags.SPAN',
} as const;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TagsValues = [...Object.values(Tags)] as const;

export type TagName = typeof TagsValues[number];

/**
 * Label component props
 */
export interface LabelProps extends NativeLabelProps {
    /**
     * Label caption
     * @deprecated It will be removed in 6 months 09-20-2025. use children instead
     */
    caption?: ReactNode,
    /**
     * Ellipsis for too long text
     */
    ellipsis?: boolean,
    /**
     * Property for additional classes
     * @deprecated It will be removed in 6 months 09-20-2025. use className or rootProps.className instead
     */
    mix?: string,
    /**
     * Ref to root element
     */
    rootRef?: React.Ref<HTMLDivElement>,
    /**
     * Props for root element
     */
    rootProps?: ComponentPropsWithoutRef<'div'>,
    /**
     * Used tag for label (div/span/label)
     */
    slug?: TagName,
    /**
     * Tooltip caption (is visible only, if Label has ellipsis)
     */
    title?: string,
}


/**
 * Accessibility
 *
 * Whenever possible, use the `Label component` to associate text with form elements explicitly.
 * The `for` (`htmlFor`) attribute of the label must exactly match the id of the form control.
 * @see [Labeling Controls in Forms Tutorial](https://www.w3.org/WAI/tutorials/forms/labels/).
 */
export const Label = forwardRef<HTMLLabelElement, LabelProps>(function Label({
    caption,
    ellipsis = true,
    mix,
    slug = Tags.LABEL as TagName,
    title,
    children,
    className,
    rootRef,
    rootProps,
    ...props
}, ref) {

    const labelClasses = classNames(
        'label', mix, rootProps?.className,
        {
            label_label: Tags.LABEL === slug,
            label_text: Tags.SPAN === slug,
            label_ellipsis: ellipsis && !title,
        },
    );

    return (
        <div
            ref={rootRef}
            {...rootProps}
            className={labelClasses}>
            <LabelBase
                {...props}
                ref={ref}
                className={classNames(className, 'label__item')}
                ellipsis={ellipsis}
                title={title}>
                {caption || children}
            </LabelBase>
        </div>
    );
});

Label.displayName = 'Label';
